import { useEffect, useState } from 'react';

import {
  Alert,
  Badge,
  Button,
  Checkbox,
  Group,
  Modal,
  Radio,
  Select,
  Stack,
  Text,
  TextInput
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { notifications } from '@mantine/notifications';
import dayjs from 'dayjs';

import { CoverageResult, PayerRow, payerResultLine, payerTitle, runCoverage, searchPayers } from '@/services/payerLookup';
import { useGetPatientQuery, useCreatePatientInsuranceMutation, useGetPatientInsurancesQuery, useUpdatePatientInsuranceMutation, useUpdatePatientOverviewMutation } from '@/store/slices/api/patientsApiSlice';
import { accountBillingType } from '@/utils/enrollmentChecklist/enrollmentSettings';
import { formatUsZip, usZipError } from '@/utils/numericFields';
import { HIERARCHY_LABEL, InsuranceHierarchy, InsurancePolicy, sortPolicies } from '@/types/insurance';
import { PatientUpdateOverviewDetails } from '@/types/patient';

import FinancialAssistanceFlow from './FinancialAssistanceModal';
import { showsFinancialAssistance } from './financialAssistance';

type Action = 'discovery' | 'eligibility' | 'manual' | 'selfpay' | 'workers' | 'assistance';
type Step = 'choose' | 'demographics' | 'map' | 'unavailable' | 'results' | 'manual' | 'selfpay' | 'workers' | 'assistance' | 'done';

const ACTIONS: { value: Action; label: string; hint: string }[] = [
  {
    value: 'discovery',
    label: 'Insurance Discovery',
    hint: 'Uses the name and address to search for active insurance.'
  },
  {
    value: 'eligibility',
    label: 'Eligibility Check',
    hint: 'Check whether coverage already on file is still active.'
  },
  {
    value: 'manual',
    label: 'Manually Add/Edit Insurance',
    hint: 'Add or edit a plan when you know the payer, member ID, and group.'
  },
  {
    value: 'selfpay',
    label: 'Mark as Self-Pay',
    hint: 'Patient receives a statement for $25 per encounter.'
  },
  {
    value: 'workers',
    label: "Add Workers' Comp",
    hint: "Add known workers' comp as the primary payer."
  },
  {
    value: 'assistance',
    label: 'Financial Assistance',
    hint: 'Apply a waiver from household income. This sets the financial override.'
  }
];

const SELF_PAY_DISCLOSURE =
  'You are choosing to opt in to Remote Monitoring Services provided by Rhythm for your implanted cardiac device. These services help support your physician in managing your care. A fee of $25 will be charged for each report processed by Rhythm. You can choose to opt out at any time by contacting Rhythm and requesting to stop. Do I have your consent to proceed?';

export function PayerSearch({
  label,
  workersComp = false,
  requireTradingPartner = false,
  value,
  onChange
}: {
  label: string;
  workersComp?: boolean;
  requireTradingPartner?: boolean;
  value: PayerRow | null;
  onChange: (row: PayerRow | null) => void;
}) {
  const [term, setTerm] = useState(value?.payerName ?? '');
  const [matches, setMatches] = useState<PayerRow[]>([]);
  const [lookup, setLookup] = useState<'idle' | 'loading' | 'ready' | 'empty' | 'error'>('idle');

  useEffect(() => {
    let cancelled = false;
    const query = term.trim();
    if (query.length < 3) {
      setMatches([]);
      setLookup('idle');
      return;
    }
    setLookup('loading');
    void searchPayers(query, { workersComp, requireTradingPartner })
      .then((rows) => {
        if (cancelled) return;
        setMatches(rows.slice(0, 8));
        setLookup(rows.length === 0 ? 'empty' : 'ready');
      })
      .catch(() => {
        if (cancelled) return;
        setMatches([]);
        setLookup('error');
      });
    return () => {
      cancelled = true;
    };
  }, [term, workersComp, requireTradingPartner]);

  return (
    <Stack gap={6}>
      <TextInput
        label={label}
        value={term}
        placeholder="Type at least 3 letters"
        onChange={(event) => {
          const next = event.currentTarget.value;
          setTerm(next);
          if (value && next !== payerTitle(value)) onChange(null);
        }}
      />
      {matches.length > 0 && (
        <Stack gap={0}>
          {matches.map((row) => (
            <Button
              key={row.id}
              variant={value?.id === row.id ? 'light' : 'subtle'}
              justify="flex-start"
              onClick={() => {
                setTerm(payerTitle(row));
                onChange(row);
              }}>
              {payerResultLine(row)}
            </Button>
          ))}
        </Stack>
      )}
      {lookup === 'empty' && (
        <Text size="sm" c="dimmed">
          {workersComp ? 'No workers\' comp payers match that search.' : 'No payers match that search.'}
        </Text>
      )}
      {lookup === 'error' && (
        <Text size="sm" c="red">
          The payer search could not be completed.
        </Text>
      )}
      {value && (
        <Text size="sm">
          Selected: {payerResultLine(value)}
        </Text>
      )}
    </Stack>
  );
}

function describePolicy(policy?: InsurancePolicy): string {
  if (!policy) return 'None on file';
  const rank = HIERARCHY_LABEL[policy.hierarchy ?? 'other'];
  return `${policy.payerName || 'Unnamed payer'} · ${policy.subscriberId || 'no member ID'} · ${rank}`;
}

const HIERARCHY_OPTIONS = [
  { value: 'primary', label: 'Primary' },
  { value: 'secondary', label: 'Secondary' },
  { value: 'tertiary', label: 'Tertiary' },
  { value: 'other', label: 'Other' }
];

type PlanDraft = {
  key: string;
  selected: boolean;
  existingId?: string;
  hierarchy: InsuranceHierarchy;
  payerName: string;
  payerId: string;
  memberId: string;
  groupId: string;
  effectiveDate: string;
  expirationDate: string;
  active: boolean;
  matched: boolean;
};

function memberKey(value?: string | null): string {
  return (value || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
}

function toHierarchy(label?: string | null, order?: number | null): InsuranceHierarchy {
  if (order === 1) return 'primary';
  if (order === 2) return 'secondary';
  if (order === 3) return 'tertiary';
  const value = (label || '').toLowerCase();
  if (value === 'primary') return 'primary';
  if (value === 'secondary') return 'secondary';
  if (value === 'tertiary') return 'tertiary';
  return 'other';
}

function existingPolicy(policies: InsurancePolicy[], memberId: string): InsurancePolicy | undefined {
  const key = memberKey(memberId);
  if (!key) return undefined;
  return policies.find((policy) => memberKey(policy.subscriberId) === key);
}

function discoveryDrafts(result: CoverageResult, policies: InsurancePolicy[]): PlanDraft[] {
  return (result.insuranceDiscovery || []).map((row, index) => {
    const match = existingPolicy(policies, row.memberId);
    return {
      key: `${row.tradingPartnerId}-${row.memberId}-${index}`,
      selected: row.active,
      existingId: match?.id,
      hierarchy: toHierarchy(row.hierarchy),
      payerName: row.payer || match?.payerName || '',
      payerId: row.payerId || match?.payerId || '',
      memberId: row.memberId || '',
      groupId: row.group || match?.groupId || '',
      effectiveDate: row.effective || '',
      expirationDate: row.end || '',
      active: row.active,
      matched: true
    };
  });
}

function eligibilityDrafts(result: CoverageResult, primary: InsurancePolicy | undefined, mapped: PayerRow | null): PlanDraft[] {
  return (result.eligibilityVerification || []).map((row, index) => {
    const returned = row.returned;
    const memberId = returned.memberId || primary?.subscriberId || '';
    return {
      key: `eligibility-${index}`,
      selected: row.match.matchedToResponse,
      existingId: primary?.id,
      hierarchy: toHierarchy(returned.hierarchy || primary?.hierarchy, returned.COBOrder),
      payerName: returned.payer || mapped?.payerName || primary?.payerName || '',
      payerId: returned.payerId || mapped?.payerId || primary?.payerId || '',
      memberId,
      groupId: returned.group || primary?.groupId || '',
      effectiveDate: returned.effective || '',
      expirationDate: returned.end || '',
      active: row.match.matchedToResponse ? returned.isActiveCoverage : primary?.active ?? false,
      matched: row.match.matchedToResponse
    };
  });
}

const InsuranceLookupModal = ({
  opened,
  onClose,
  patientId
}: {
  opened: boolean;
  onClose: () => void;
  patientId: string;
}) => {
  const { data: patient } = useGetPatientQuery(patientId, { skip: !opened });
  const { data: policies = [] } = useGetPatientInsurancesQuery(patientId, { skip: !opened });
  const [createInsurance, { isLoading: creating }] = useCreatePatientInsuranceMutation();
  const [updateInsurance, { isLoading: updating }] = useUpdatePatientInsuranceMutation();
  const [updateOverview, { isLoading: updatingPatient }] = useUpdatePatientOverviewMutation();
  const [step, setStep] = useState<Step>('choose');
  const [action, setAction] = useState<Action>('discovery');
  const [dateOfService, setDateOfService] = useState<Date | null>(new Date());
  const [draft, setDraft] = useState({ givenName: '', familyName: '', birthDate: '', address1: '', city: '', state: '', zip: '' });
  const [mappedPayer, setMappedPayer] = useState<PayerRow | null>(null);
  const [primaryPayer, setPrimaryPayer] = useState<PayerRow | null>(null);
  const [primaryMember, setPrimaryMember] = useState('');
  const [primaryGroup, setPrimaryGroup] = useState('');
  const [addSecondary, setAddSecondary] = useState(false);
  const [secondaryPayer, setSecondaryPayer] = useState<PayerRow | null>(null);
  const [secondaryMember, setSecondaryMember] = useState('');
  const [selfPayConfirmed, setSelfPayConfirmed] = useState<string | null>(null);
  const [compPayer, setCompPayer] = useState<PayerRow | null>(null);
  const [compClaim, setCompClaim] = useState('');
  const [saved, setSaved] = useState('');
  const [coverage, setCoverage] = useState<CoverageResult | null>(null);
  const [checking, setChecking] = useState(false);
  const [resultKind, setResultKind] = useState<'discovery' | 'eligibility'>('discovery');
  const [plans, setPlans] = useState<PlanDraft[]>([]);
  const [demoChoice, setDemoChoice] = useState<'keep' | 'update'>('keep');

  const active = sortPolicies(policies).filter((policy) => policy.active);
  const primary = active.find((policy) => policy.hierarchy === 'primary') ?? active[0];

  useEffect(() => {
    if (!opened) return;
    setStep('choose');
    setAction('discovery');
    setDateOfService(new Date());
    setMappedPayer(null);
    setPrimaryPayer(null);
    setPrimaryMember('');
    setPrimaryGroup('');
    setAddSecondary(false);
    setSecondaryPayer(null);
    setSecondaryMember('');
    setSelfPayConfirmed(null);
    setCompPayer(null);
    setCompClaim('');
    setSaved('');
    setCoverage(null);
    setChecking(false);
    setResultKind('discovery');
    setPlans([]);
    setDemoChoice('keep');
  }, [opened]);

  useEffect(() => {
    if (!opened || !patient) return;
    setDraft({
      givenName: patient.givenName ?? '',
      familyName: patient.familyName ?? '',
      birthDate: patient.birthDate ? dayjs(patient.birthDate).format('YYYY-MM-DD') : '',
      address1: patient.address?.line1 ?? '',
      city: patient.address?.city ?? '',
      state: patient.address?.state ?? '',
      zip: formatUsZip(patient.address?.postalCode ?? '')
    });
  }, [opened, patient]);

  const zipError = usZipError(draft.zip);
  const missingDemographics =
    !draft.givenName || !draft.familyName || !draft.birthDate || !draft.address1 || !draft.city || !draft.state || !draft.zip || Boolean(zipError);

  const fail = (error: unknown) => {
    const message = (error as { data?: { message?: string | string[] } })?.data?.message;
    notifications.show({
      title: 'Insurance',
      message: Array.isArray(message) ? message.join(' ') : message || 'Could not save this insurance.',
      color: 'red'
    });
  };

  const finish = (message: string) => {
    setSaved(message);
    setStep('done');
  };

  const runCheck = async (kind: 'discovery' | 'eligibility') => {
    setChecking(true);
    try {
      const result = await runCoverage({
        accountNumber: patient?.rhythmId || patientId,
        dateOfService: dateOfService ? dayjs(dateOfService).format('YYYY-MM-DD') : undefined,
        demographics: {
          Patient_First_Name__c: draft.givenName,
          Patient_Last_Name__c: draft.familyName,
          Patient_Date_of_Birth__c: draft.birthDate,
          Patient_Address_Line_1__c: draft.address1,
          Patient_City__c: draft.city,
          Patient_State__c: draft.state,
          Patient_Zip_Code__c: draft.zip
        },
        insurances: kind === 'eligibility'
          ? [{
              FHRC_Trading_Partner_ID__c: mappedPayer?.tradingPartnerId || '',
              Member_ID__c: primary?.subscriberId || '',
              Hierarchy__c: 'Primary'
            }]
          : [],
        options: {
          runInsuranceDiscovery: kind === 'discovery',
          checkEligibility: kind === 'eligibility',
          checkDemographics: kind === 'discovery'
        }
      });
      setCoverage(result);
      setResultKind(kind);
      setDemoChoice('keep');
      setPlans(kind === 'discovery' ? discoveryDrafts(result, policies) : eligibilityDrafts(result, primary, mappedPayer));
      setStep('results');
    } catch {
      setStep('unavailable');
    } finally {
      setChecking(false);
    }
  };

  const patchPlan = (key: string, patch: Partial<PlanDraft>) => {
    setPlans((current) => current.map((plan) => (plan.key === key ? { ...plan, ...patch } : plan)));
  };

  const goNext = () => {
    if (action === 'discovery') {
      if (missingDemographics) setStep('demographics');
      else void runCheck('discovery');
      return;
    }
    if (action === 'eligibility') {
      setStep('map');
      return;
    }
    if (action === 'manual') setStep('manual');
    if (action === 'selfpay') setStep('selfpay');
    if (action === 'workers') setStep('workers');
    if (action === 'assistance') setStep('assistance');
  };

  const saveManual = async () => {
    if (!primaryPayer || !primaryMember) return;
    try {
      await createInsurance({
        patientId,
        body: {
          payerName: primaryPayer.payerName,
          payerId: primaryPayer.payerId,
          subscriberId: primaryMember.trim(),
          groupId: primaryGroup.trim() || null,
          hierarchy: 'primary',
          active: true,
          subscriberRelationship: 'self',
          moveExistingToOther: true
        }
      }).unwrap();
      if (addSecondary && secondaryPayer && secondaryMember) {
        await createInsurance({
          patientId,
          body: {
            payerName: secondaryPayer.payerName,
            payerId: secondaryPayer.payerId,
            subscriberId: secondaryMember.trim(),
            hierarchy: 'secondary',
            active: true,
            subscriberRelationship: 'self',
            moveExistingToOther: true
          }
        }).unwrap();
      }
      finish(
        `Saved ${primaryPayer.payerName} as primary${addSecondary && secondaryPayer ? ` and ${secondaryPayer.payerName} as secondary` : ''}.`
      );
    } catch (error) {
      fail(error);
    }
  };

  const saveSelfPay = async () => {
    try {
      await createInsurance({
        patientId,
        body: {
          payerName: 'Self Pay',
          planName: 'Self Pay',
          subscriberId: 'Self Pay',
          hierarchy: 'primary',
          active: true,
          subscriberRelationship: 'self',
          moveExistingToOther: true
        }
      }).unwrap();
      finish('Marked the patient self-pay at $25 per encounter.');
    } catch (error) {
      fail(error);
    }
  };

  const saveWorkers = async () => {
    if (!compPayer || !compClaim.trim()) return;
    try {
      await createInsurance({
        patientId,
        body: {
          payerName: compPayer.payerName,
          payerId: compPayer.payerId,
          subscriberId: compClaim.trim(),
          hierarchy: 'primary',
          active: true,
          subscriberRelationship: 'self',
          moveExistingToOther: true
        }
      }).unwrap();
      finish(`Added ${compPayer.payerName} worker's comp claim ${compClaim.trim()} as primary.`);
    } catch (error) {
      fail(error);
    }
  };

  const savePayerMatch = async () => {
    if (!primary || !mappedPayer) return;
    try {
      await updateInsurance({
        patientId,
        insuranceId: primary.id,
        body: { payerId: mappedPayer.payerId, payerName: mappedPayer.payerName }
      }).unwrap();
      finish(`Saved ${mappedPayer.payerName} as the payer on the primary plan.`);
    } catch (error) {
      fail(error);
    }
  };

  const acceptResults = async () => {
    const chosen = plans.filter((plan) => plan.selected);
    if (chosen.length === 0) return;
    try {
      for (const plan of chosen) {
        const body = {
          payerName: plan.payerName.trim() || null,
          payerId: plan.payerId.trim() || null,
          subscriberId: plan.memberId.trim() || null,
          groupId: plan.groupId.trim() || null,
          hierarchy: plan.hierarchy,
          active: plan.active,
          effectiveDate: plan.effectiveDate || null,
          expirationDate: plan.expirationDate || null,
          subscriberRelationship: 'self' as const,
          moveExistingToOther: plan.active && plan.hierarchy !== 'other'
        };
        if (plan.existingId) {
          await updateInsurance({ patientId, insuranceId: plan.existingId, body }).unwrap();
        } else {
          await createInsurance({ patientId, body }).unwrap();
        }
      }
      let demoNote = ' Name and address were left as they are.';
      if (resultKind === 'discovery' && demoChoice === 'update') {
        const found = coverage?.demographics.validatedFields ?? {};
        const overview: PatientUpdateOverviewDetails = {
          givenName: found.Patient_First_Name__c || draft.givenName,
          familyName: found.Patient_Last_Name__c || draft.familyName,
          birthDate: found.Patient_Date_of_Birth__c || draft.birthDate,
          address: {
            id: patient?.address?.id,
            line1: found.Patient_Address_Line_1__c || draft.address1,
            city: found.Patient_City__c || draft.city,
            state: found.Patient_State__c || draft.state,
            postalCode: found.Patient_Zip_Code__c || draft.zip
          } as PatientUpdateOverviewDetails['address']
        };
        await updateOverview({ id: patientId, body: overview }).unwrap();
        demoNote = ' Name and address were updated from the search.';
      }
      const names = chosen.map((plan) => plan.payerName || 'Unnamed payer').join(', ');
      finish(`Saved ${chosen.length} plan${chosen.length === 1 ? '' : 's'}: ${names}.${resultKind === 'discovery' ? demoNote : ''}`);
    } catch (error) {
      fail(error);
    }
  };

  const editSelectedManually = () => {
    const plan = plans.find((item) => item.selected) ?? plans[0];
    if (!plan) {
      setStep('manual');
      return;
    }
    setPrimaryMember(plan.memberId);
    setPrimaryGroup(plan.groupId);
    setPrimaryPayer({
      id: plan.payerId || plan.payerName || plan.key,
      payerName: plan.payerName,
      payerId: plan.payerId,
      tradingPartnerId: '',
      tradingPartnerName: '',
      workersComp: false
    });
    setStep('manual');
  };

  const dos = dateOfService ? dayjs(dateOfService).format('MMM D, YYYY') : 'today';
  const saving = creating || updating || updatingPatient;
  const selectedPlans = plans.filter((plan) => plan.selected);
  const ranked = selectedPlans.map((plan) => plan.hierarchy).filter((hierarchy) => hierarchy !== 'other');
  const duplicateRank = ranked.length !== new Set(ranked).size;
  const selectedReady = selectedPlans.length > 0 && selectedPlans.every((plan) => plan.payerName.trim() && plan.memberId.trim());
  const foundFields = coverage?.demographics.validatedFields ?? {};
  const foundAddress = [foundFields.Patient_Address_Line_1__c, foundFields.Patient_City__c, foundFields.Patient_State__c, foundFields.Patient_Zip_Code__c]
    .filter(Boolean)
    .join(', ');
  const onFileAddress = [draft.address1, draft.city, draft.state, draft.zip].filter(Boolean).join(', ');

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Add & Edit Insurance"
      size="lg"
      centered
      styles={{
        header: { paddingLeft: 28, paddingRight: 20 },
        body: { paddingLeft: 28, paddingRight: 24, paddingBottom: 20 }
      }}>
      {step === 'choose' && (
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            Find coverage for this patient, or confirm a plan already on file. Current primary: {describePolicy(primary)}.
          </Text>
          <Radio.Group value={action} onChange={(value) => setAction(value as Action)}>
            <Stack gap="sm">
              {ACTIONS.filter((item) => item.value !== 'assistance' || showsFinancialAssistance(accountBillingType(patient?.clinic))).map((item) => (
                <Radio key={item.value} value={item.value} label={item.label} description={item.hint} />
              ))}
            </Stack>
          </Radio.Group>
          {action !== 'assistance' && (
            <DatePickerInput label="Date of service" value={dateOfService} onChange={setDateOfService} maxDate={new Date()} w={220} />
          )}
          <Group justify="flex-end">
            <Button variant="default" onClick={onClose}>
              Cancel
            </Button>
            <Button loading={checking} onClick={goNext}>Continue</Button>
          </Group>
        </Stack>
      )}

      {step === 'assistance' && patient && (
        <FinancialAssistanceFlow
          patientId={patientId}
          override={patient.financialPayerOverride ?? null}
          onBack={() => setStep('choose')}
          onClose={onClose}
        />
      )}
      {step === 'assistance' && !patient && (
        <Text size="sm" c="dimmed">
          Loading patient billing…
        </Text>
      )}

      {step === 'demographics' && (
        <Stack gap="md">
          <Alert color="yellow" title="Name and address required">
            A search needs the patient's name, date of birth, and address.
          </Alert>
          <Group grow>
            <TextInput label="First name" value={draft.givenName} onChange={(event) => setDraft({ ...draft, givenName: event.currentTarget.value })} />
            <TextInput label="Last name" value={draft.familyName} onChange={(event) => setDraft({ ...draft, familyName: event.currentTarget.value })} />
          </Group>
          <TextInput label="Date of birth" value={draft.birthDate} onChange={(event) => setDraft({ ...draft, birthDate: event.currentTarget.value })} />
          <TextInput label="Address" value={draft.address1} onChange={(event) => setDraft({ ...draft, address1: event.currentTarget.value })} />
          <Group grow>
            <TextInput label="City" value={draft.city} onChange={(event) => setDraft({ ...draft, city: event.currentTarget.value })} />
            <TextInput label="State" value={draft.state} onChange={(event) => setDraft({ ...draft, state: event.currentTarget.value })} />
            <TextInput
              label="Zip"
              inputMode="numeric"
              value={draft.zip}
              error={zipError}
              onChange={(event) => setDraft({ ...draft, zip: formatUsZip(event.currentTarget.value) })}
            />
          </Group>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setStep('choose')}>
              Back
            </Button>
            <Button disabled={missingDemographics} loading={checking} onClick={() => void runCheck('discovery')}>
              Run discovery
            </Button>
          </Group>
        </Stack>
      )}

      {step === 'map' && (
        <Stack gap="md">
          <Alert color="yellow" title="Match the payer">
            {describePolicy(primary)} has no payer ID. Search the payer list before an eligibility check.
          </Alert>
          <PayerSearch label="Payer" requireTradingPartner value={mappedPayer} onChange={setMappedPayer} />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setStep('choose')}>
              Back
            </Button>
            <Button disabled={!mappedPayer} loading={saving} onClick={() => void savePayerMatch()}>
              Save payer match
            </Button>
            <Button disabled={!mappedPayer || !primary?.subscriberId} loading={checking} onClick={() => void runCheck('eligibility')}>
              Check eligibility
            </Button>
          </Group>
        </Stack>
      )}

      {step === 'unavailable' && (
        <Stack gap="md">
          <Alert color="yellow" title="Coverage check did not complete">
            The lookup for {dos} did not return a result. You can still add or edit a plan by hand.
          </Alert>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setStep('choose')}>
              Back
            </Button>
            <Button onClick={() => setStep('manual')}>Add or edit manually</Button>
          </Group>
        </Stack>
      )}

      {step === 'results' && (
        <Stack gap="md">
          <Text fw={600}>{resultKind === 'discovery' ? 'Coverage found' : 'Eligibility'}</Text>
          <Text size="sm">
            {resultKind === 'discovery'
              ? `${coverage?.summary.discoveredEligibilityCount || 0} plans returned. Choose the ones to write, and change any field before saving.`
              : `${coverage?.summary.matchedInsuranceCount || 0} matched. Choose what to write back to the chart.`}
          </Text>
          {(coverage?.messages || []).map((item, index) => (
            <Alert key={index} color={item.severity === 'ERROR' ? 'red' : 'yellow'}>
              {item.message}
            </Alert>
          ))}
          {resultKind === 'discovery' && (foundAddress || foundFields.Patient_First_Name__c) && (
            <Stack gap="sm">
              <Group align="stretch" grow>
                <Stack gap={4}>
                  <Text size="sm" fw={600}>On file</Text>
                  <Text size="sm">{draft.givenName} {draft.familyName}</Text>
                  <Text size="sm">Date of birth: {draft.birthDate || '—'}</Text>
                  <Text size="sm">Address: {onFileAddress || '—'}</Text>
                </Stack>
                <Stack gap={4}>
                  <Text size="sm" fw={600}>Found</Text>
                  <Text size="sm">{foundFields.Patient_First_Name__c || draft.givenName} {foundFields.Patient_Last_Name__c || draft.familyName}</Text>
                  <Text size="sm">Date of birth: {foundFields.Patient_Date_of_Birth__c || draft.birthDate || '—'}</Text>
                  <Text size="sm">Address: {foundAddress || '—'}</Text>
                </Stack>
              </Group>
              <Radio.Group label="Demographics" value={demoChoice} onChange={(value) => setDemoChoice(value as 'keep' | 'update')}>
                <Group mt="xs">
                  <Radio value="update" label="Use the name and address that were found" />
                  <Radio value="keep" label="Keep the name and address on file" />
                </Group>
              </Radio.Group>
            </Stack>
          )}
          {plans.length === 0 && (
            <Alert color="yellow" title="Nothing to write">
              {resultKind === 'eligibility'
                ? 'This plan was not confirmed. Run discovery to search by name and address, or add the plan by hand.'
                : 'No medical plan came back. Add one by hand, or keep what is on file.'}
            </Alert>
          )}
          {plans.map((plan) => (
            <Stack key={plan.key} gap="sm" p="sm" style={{ border: '1px solid var(--mantine-color-gray-3)', borderRadius: 8 }}>
              <Group justify="space-between">
                <Checkbox
                  checked={plan.selected}
                  label="Write this plan"
                  onChange={(event) => patchPlan(plan.key, { selected: event.currentTarget.checked })}
                />
                <Group gap={6}>
                  <Badge variant="light" color={plan.active ? 'teal' : 'gray'}>{plan.active ? 'Active' : 'Inactive'}</Badge>
                  {resultKind === 'eligibility' && (
                    <Badge variant="light" color={plan.matched ? 'teal' : 'yellow'}>{plan.matched ? 'Matched' : 'Not matched'}</Badge>
                  )}
                </Group>
              </Group>
              <Group grow>
                <TextInput label="Payer" value={plan.payerName} onChange={(event) => patchPlan(plan.key, { payerName: event.currentTarget.value })} />
                <TextInput label="Payer ID" value={plan.payerId} onChange={(event) => patchPlan(plan.key, { payerId: event.currentTarget.value })} />
              </Group>
              <Group grow>
                <TextInput label="Member ID" value={plan.memberId} onChange={(event) => patchPlan(plan.key, { memberId: event.currentTarget.value })} />
                <TextInput label="Group" value={plan.groupId} onChange={(event) => patchPlan(plan.key, { groupId: event.currentTarget.value })} />
              </Group>
              <Group grow>
                <Select
                  label="Rank"
                  data={HIERARCHY_OPTIONS}
                  value={plan.hierarchy}
                  allowDeselect={false}
                  onChange={(value) => value && patchPlan(plan.key, { hierarchy: value as InsuranceHierarchy })}
                />
                <TextInput label="Effective" placeholder="YYYY-MM-DD" value={plan.effectiveDate} onChange={(event) => patchPlan(plan.key, { effectiveDate: event.currentTarget.value })} />
              </Group>
              <Checkbox
                checked={plan.active}
                label="Active coverage"
                onChange={(event) => patchPlan(plan.key, { active: event.currentTarget.checked })}
              />
            </Stack>
          ))}
          {duplicateRank && (
            <Alert color="yellow">Choose one Primary, one Secondary, and one Tertiary. Extra plans can be Other.</Alert>
          )}
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setStep(resultKind === 'eligibility' ? 'map' : 'choose')}>
              Back
            </Button>
            <Button variant="default" onClick={() => finish('Kept the insurance on file.')}>
              Keep existing
            </Button>
            {resultKind === 'eligibility' && (
              <Button variant="default" loading={checking} onClick={() => void runCheck('discovery')}>
                Run discovery
              </Button>
            )}
            <Button variant="default" onClick={editSelectedManually}>
              Edit further
            </Button>
            <Button
              loading={saving}
              disabled={!selectedReady || duplicateRank}
              onClick={() => void acceptResults()}>
              Accept and save
            </Button>
          </Group>
        </Stack>
      )}

      {step === 'manual' && (
        <Stack gap="md">
          <Text size="sm">Current insurance on file: {describePolicy(primary)}.</Text>
          <Text fw={600}>Primary insurance</Text>
          <PayerSearch label="Payer" value={primaryPayer} onChange={setPrimaryPayer} />
          <Group grow>
            <TextInput label="Member ID" value={primaryMember} onChange={(event) => setPrimaryMember(event.currentTarget.value)} />
            <TextInput label="Group" value={primaryGroup} onChange={(event) => setPrimaryGroup(event.currentTarget.value)} />
          </Group>
          <Checkbox label="Add secondary insurance" checked={addSecondary} onChange={(event) => setAddSecondary(event.currentTarget.checked)} />
          {addSecondary && (
            <Group grow align="flex-start">
              <PayerSearch label="Secondary payer" value={secondaryPayer} onChange={setSecondaryPayer} />
              <TextInput label="Secondary member ID" value={secondaryMember} onChange={(event) => setSecondaryMember(event.currentTarget.value)} />
            </Group>
          )}
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setStep('choose')}>
              Back
            </Button>
            <Button
              loading={saving}
              disabled={!primaryPayer || !primaryMember.trim() || (addSecondary && (!secondaryPayer || !secondaryMember.trim()))}
              onClick={() => void saveManual()}>
              Save
            </Button>
          </Group>
        </Stack>
      )}

      {step === 'selfpay' && (
        <Stack gap="md">
          <Alert color="yellow" title="Read this to the patient">
            {SELF_PAY_DISCLOSURE}
          </Alert>
          <Select
            label="Patient consent"
            placeholder="Select"
            data={[{ value: 'confirmed', label: 'Confirmed' }]}
            value={selfPayConfirmed}
            onChange={setSelfPayConfirmed}
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setStep('choose')}>
              Back
            </Button>
            <Button loading={saving} disabled={selfPayConfirmed !== 'confirmed'} onClick={() => void saveSelfPay()}>
              Finish
            </Button>
          </Group>
        </Stack>
      )}

      {step === 'workers' && (
        <Stack gap="md">
          <Text size="sm">Workers' comp will be the primary payer.</Text>
          <PayerSearch label="Carrier" workersComp value={compPayer} onChange={setCompPayer} />
          <TextInput label="Claim number" value={compClaim} onChange={(event) => setCompClaim(event.currentTarget.value)} />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setStep('choose')}>
              Back
            </Button>
            <Button loading={saving} disabled={!compPayer || !compClaim.trim()} onClick={() => void saveWorkers()}>
              Save
            </Button>
          </Group>
        </Stack>
      )}

      {step === 'done' && (
        <Stack gap="md">
          <Alert color="green" title="Insurance updated">
            {saved}
          </Alert>
          <Group justify="flex-end">
            <Button onClick={onClose}>Close</Button>
          </Group>
        </Stack>
      )}
    </Modal>
  );
};

export default InsuranceLookupModal;
